import type { ReactNode } from 'react';
import React from 'react';
import { Box, Divider, Icon, MediaObject, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconChecklistFilled } from '@zillow/constellation-icons';
import { SatelliteUiTreatmentType } from '@zg-rentals/ts-satellite-client';
import { chatBubbleStyles } from '../../ChatBubble/styles';
import { systemMessageBubbleStyles } from './styles';
import ChatAvatar from '../../ChatAvatar';
import MessageFooter from '../../MessageFooter';
import {
  LANDLORD_SYSTEM_MESSAGE_ICON_MAP,
  RENTER_SYSTEM_MESSAGE_ICON_MAP,
} from '../../../constants/systemMessageIcons';
import { normalizeUrl } from '../../../utils/urlUtils';
import type { Persona } from '../../../types/persona';

export type HighPrioritySystemMessageProps = {
  messageDateMs: string;
  messageType: string;
  uiTreatmentType: string;
  ctaText?: string;
  message: string;
  onCtaClick?: () => void;
  renderLink?: (children: ReactNode) => ReactNode;
  shouldDisplayAsPreview?: boolean;
  title: string;
  url?: string;
  persona?: Persona;
};

export default function HighPrioritySystemMessage({
  ctaText,
  message,
  messageDateMs,
  messageType,
  onCtaClick,
  renderLink,
  persona = 'landlord',
  shouldDisplayAsPreview = false,
  title,
  uiTreatmentType,
  url,
}: HighPrioritySystemMessageProps) {
  const hasTitle = !!title;
  const hasMessage = !!message;
  const hasCtaText = !!ctaText;
  const ctaHref = url ? normalizeUrl(url) : null;
  // A nav CTA with an unusable URL and no fallback action would render a dead control; hide it
  const hasUsableCta = !!renderLink || !!ctaHref || !!onCtaClick;
  const showCta = hasCtaText && (shouldDisplayAsPreview || hasUsableCta);

  const renderCta = () => {
    if (renderLink) {
      return renderLink(ctaText);
    }
    if (ctaHref) {
      return (
        <TextButton asChild textStyle="body">
          <a href={ctaHref} target="_blank" rel="noopener noreferrer" onClick={onCtaClick}>
            {ctaText}
          </a>
        </TextButton>
      );
    }
    return (
      <TextButton onClick={onCtaClick} textStyle="body">
        {ctaText}
      </TextButton>
    );
  };

  // Determine if icon should be shown based on uiTreatmentType — accepts both the legacy
  // camelCase form (GraphQL-shaped consumers, SRW) and the Proxit enum casing (SLW).
  const shouldShowIcon =
    uiTreatmentType?.includes('highPriorityWithLogo') ||
    uiTreatmentType === SatelliteUiTreatmentType.HIGH_PRIORITY_W_LOGO;

  // Get icon from persona-specific mapping
  const iconMap = persona === 'renter' ? RENTER_SYSTEM_MESSAGE_ICON_MAP : LANDLORD_SYSTEM_MESSAGE_ICON_MAP;
  const IconComponent = iconMap[messageType as keyof typeof iconMap] ?? IconChecklistFilled;

  return (
    <MediaObject
      media={<ChatAvatar type="zillow" hasTimestampInMessage={!shouldDisplayAsPreview} />}
      css={{
        alignItems: 'end',
        gap: 0,
      }}
    >
      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          data-testid="high-priority-system-message"
          css={{
            ...chatBubbleStyles,
            ...systemMessageBubbleStyles,
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'row', gap: 'default', padding: 'default' }}>
            {shouldShowIcon && (
              <Icon
                role="img"
                size="xl"
                css={{
                  color: 'icon.express.inspire.hero',
                  alignSelf: 'center',
                }}
              >
                {React.createElement(IconComponent)}
              </Icon>
            )}
            <Box>
              {hasTitle && <Paragraph textStyle="body-bold">{title}</Paragraph>}
              {hasMessage && <Paragraph>{message}</Paragraph>}
            </Box>
          </Box>

          {showCta && <Divider css={{ backgroundColor: 'border.muted' }} />}
          {showCta && !shouldDisplayAsPreview && (
            <Box
              css={{
                padding: 'default',
                textAlign: 'center',
              }}
            >
              {renderCta()}
            </Box>
          )}
          {shouldDisplayAsPreview && hasCtaText && (
            <Paragraph
              css={{
                color: 'blue.400',
                padding: 'default',
                textAlign: 'center',
              }}
            >
              {ctaText}
            </Paragraph>
          )}
        </Box>
        {!!messageDateMs && (
          <MessageFooter isMessageOwner={false} messageDateMs={messageDateMs} metaLabelType="system" />
        )}
      </Box>
    </MediaObject>
  );
}
